import React, { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretRight, faCircle, faPlus, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { RequirementGroup, RequirementImage, RequirementItem, Rubric } from './calculatorApi';
import { Instance, InstanceMap } from './instanceModel';
import { OptionsInput } from './OptionsInput';
import { CalcImageData, ImageThumb } from './CalcImage';

export interface TreeMutators {
    addInstance: (reqId: string) => void;
    removeInstance: (reqId: string, iid: number) => void;
    updateInstance: (reqId: string, iid: number, patch: Partial<Instance>) => void;
    updateOpt: (reqId: string, iid: number, fn: (prev: Record<string, number>) => Record<string, number>) => void;
}

interface TreeProps extends TreeMutators {
    tree: RequirementGroup;
    rubrics: Rubric[];
    instances: InstanceMap;
    configured: Set<string>;
    reqOrder: 'xml' | 'name';
}

const toImageData = (images: RequirementImage[]): CalcImageData[] =>
    (images || []).map((i) => ({
        articleNumber: i.articleNumber,
        name: i.name,
        titel: i.titel,
        beschreibung: i.beschreibung,
        haupt: i.haupt,
        crop: i.crop,
    }));

const orderItems = (items: RequirementItem[], reqOrder: string): RequirementItem[] => {
    if (reqOrder !== 'name') return items;
    return [...items].sort(
        (a, b) =>
            (a.description || a.id).localeCompare(b.description || b.id, 'de', { sensitivity: 'base' }) ||
            a.id.localeCompare(b.id),
    );
};

const orderGroups = (groups: RequirementGroup[], reqOrder: string): RequirementGroup[] => {
    if (reqOrder !== 'name') return groups;
    return [...groups].sort((a, b) => (a.title || '').localeCompare(b.title || '', 'de', { sensitivity: 'base' }));
};

const groupHasAg = (group: RequirementGroup, ag: string): boolean => {
    if (group.items.some((i) => !i.implicit && i.articleGroup === ag)) return true;
    return group.groups.some((s) => groupHasAg(s, ag));
};

const groupHasConfigured = (group: RequirementGroup, ag: string, configured: Set<string>): boolean => {
    if (group.items.some((i) => !i.implicit && i.articleGroup === ag && configured.has(i.id))) return true;
    return group.groups.some((s) => groupHasConfigured(s, ag, configured));
};

const ConfiguredDot: React.FC = () => (
    <FontAwesomeIcon icon={faCircle} style={{ fontSize: 8, marginLeft: 6, color: 'var(--mui-palette-success-main, #2e7d32)' }} />
);

/** Eine einzelne Instanz (Variante) einer Anforderung. */
const InstanceRow: React.FC<{
    item: RequirementItem;
    inst: Instance;
    idx: number;
    removable: boolean;
    mutators: TreeMutators;
}> = ({ item, inst, idx, removable, mutators }) => {
    const hasOptions = item.options && item.options.length > 0;

    return (
        <Box sx={{ mt: 1, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                    label="Menge"
                    type="number"
                    size="small"
                    value={inst.menge || ''}
                    onChange={(e) =>
                        mutators.updateInstance(item.id, inst.iid, { menge: Number(e.target.value) || 0 })
                    }
                    slotProps={{ htmlInput: { min: 0, step: 1, style: { width: 70 } } }}
                />
                <TextField
                    label="Bereich"
                    size="small"
                    value={inst.area}
                    onChange={(e) => mutators.updateInstance(item.id, inst.iid, { area: e.target.value.trim() || 'A' })}
                    slotProps={{ htmlInput: { maxLength: 16, style: { width: 80 } } }}
                />
                {(hasOptions || idx > 0) && (
                    <TextField
                        label="Bezeichnung (optional)"
                        size="small"
                        value={inst.setName}
                        onChange={(e) => mutators.updateInstance(item.id, inst.iid, { setName: e.target.value })}
                        sx={{ flexGrow: 1, minWidth: 160 }}
                    />
                )}
                {removable && (
                    <Tooltip title="Variante entfernen">
                        <IconButton size="small" color="error" onClick={() => mutators.removeInstance(item.id, inst.iid)}>
                            <FontAwesomeIcon icon={faXmark} />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
            {hasOptions && inst.menge > 0 && (
                <OptionsInput
                    options={item.options}
                    opt={inst.opt}
                    onChange={(fn) => mutators.updateOpt(item.id, inst.iid, fn)}
                />
            )}
        </Box>
    );
};

/** Eine Anforderung mit ihren Instanzen/Varianten. */
const RequirementRow: React.FC<{
    item: RequirementItem;
    instances: Instance[];
    configured: boolean;
    mutators: TreeMutators;
}> = ({ item, instances, configured, mutators }) => (
    <Box sx={{ mb: 1.5, p: 1, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle2">
                    {item.description || item.id}
                    {configured && <ConfiguredDot />}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {item.id}
                    {item.articleGroup ? ` · ${item.articleGroup}` : ''}
                </Typography>
            </Box>
            <ImageThumb title={item.description || item.id} images={toImageData(item.images)} />
            <Button
                size="small"
                variant="outlined"
                startIcon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => mutators.addInstance(item.id)}
            >
                Variante
            </Button>
        </Stack>
        {instances.map((inst, idx) => (
            <InstanceRow
                key={inst.iid}
                item={item}
                inst={inst}
                idx={idx}
                removable={instances.length > 1}
                mutators={mutators}
            />
        ))}
    </Box>
);

/** Einklappbare Pfad-Kategorie. */
const Category: React.FC<{
    group: RequirementGroup;
    ag: string;
    instances: InstanceMap;
    configured: Set<string>;
    reqOrder: string;
    mutators: TreeMutators;
}> = ({ group, ag, instances, configured, reqOrder, mutators }) => {
    const [open, setOpen] = useState(false);
    const hasConfigured = groupHasConfigured(group, ag, configured);
    const items = orderItems(group.items.filter((i) => !i.implicit && i.articleGroup === ag), reqOrder);
    const subGroups = orderGroups(group.groups.filter((s) => groupHasAg(s, ag)), reqOrder);

    return (
        <Box sx={{ mb: 1 }}>
            <Stack
                direction="row"
                spacing={1}
                onClick={() => setOpen((o) => !o)}
                sx={{ alignItems: 'center', cursor: 'pointer', py: 0.5, px: 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
            >
                <FontAwesomeIcon icon={open ? faCaretDown : faCaretRight} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {group.title || '(ohne Titel)'}
                </Typography>
                {hasConfigured && <ConfiguredDot />}
            </Stack>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 2, pt: 1 }}>
                    {items.map((item) => (
                        <RequirementRow
                            key={item.id}
                            item={item}
                            instances={instances[item.id] || []}
                            configured={configured.has(item.id)}
                            mutators={mutators}
                        />
                    ))}
                    {subGroups.map((sub) => (
                        <Category
                            key={sub.path}
                            group={sub}
                            ag={ag}
                            instances={instances}
                            configured={configured}
                            reqOrder={reqOrder}
                            mutators={mutators}
                        />
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

export const RequirementInputTree: React.FC<TreeProps> = (props) => {
    const { tree, rubrics, instances, configured, reqOrder } = props;
    const mutators: TreeMutators = props;

    const presentAgs = useMemo(() => {
        const present = new Set<string>();
        const collect = (g: RequirementGroup) => {
            for (const i of g.items) if (!i.implicit && i.articleGroup) present.add(i.articleGroup);
            for (const s of g.groups) collect(s);
        };
        collect(tree);
        const order = rubrics.map((r) => r.code);
        return [
            ...order.filter((a) => present.has(a)),
            ...Array.from(present).filter((a) => !order.includes(a)).sort(),
        ];
    }, [tree, rubrics]);

    const labels = useMemo(() => Object.fromEntries(rubrics.map((r) => [r.code, r.label])), [rubrics]);
    const [activeAg, setActiveAg] = useState<string>(presentAgs[0] || '');
    const ag = presentAgs.includes(activeAg) ? activeAg : presentAgs[0] || '';

    const topItems = orderItems(tree.items.filter((i) => !i.implicit && i.articleGroup === ag), reqOrder);
    const topGroups = orderGroups(tree.groups.filter((s) => groupHasAg(s, ag)), reqOrder);

    const agConfigured = useMemo(() => {
        const set = new Set<string>();
        const walk = (g: RequirementGroup) => {
            for (const i of g.items) if (!i.implicit && i.articleGroup && configured.has(i.id)) set.add(i.articleGroup);
            for (const s of g.groups) walk(s);
        };
        walk(tree);
        return set;
    }, [tree, configured]);

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                {presentAgs.map((a) => (
                    <Chip
                        key={a}
                        label={
                            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                {labels[a] || a}
                                {agConfigured.has(a) && <ConfiguredDot />}
                            </Box>
                        }
                        color={a === ag ? 'primary' : 'default'}
                        variant={a === ag ? 'filled' : 'outlined'}
                        onClick={() => setActiveAg(a)}
                    />
                ))}
            </Stack>

            {topItems.map((item) => (
                <RequirementRow
                    key={item.id}
                    item={item}
                    instances={instances[item.id] || []}
                    configured={configured.has(item.id)}
                    mutators={mutators}
                />
            ))}
            {topGroups.map((group) => (
                <Category
                    key={group.path}
                    group={group}
                    ag={ag}
                    instances={instances}
                    configured={configured}
                    reqOrder={reqOrder}
                    mutators={mutators}
                />
            ))}
        </Box>
    );
};
